import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type JSX, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DistrictDropdown } from './DistrictDropdown.tsx';
import { useDistrictFilter } from './hooks/useDistrictFilter.ts';
import { useDistrictSelection } from './hooks/useDistrictSelection.ts';
import { useDropdownState } from './hooks/useDropdownState.ts';
import { styles } from './SearchField.styles.ts';
import { SearchField } from './SearchField.tsx';
import type { CongressionalDistrictPickerProps } from './types.ts';

export const CongressionalDistrictPicker = ({
  data,
  value,
  onChange,
}: CongressionalDistrictPickerProps): JSX.Element => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { selected, handleSelectDistrict } = useDistrictSelection(value, onChange);
  const {
    open,
    expanded,
    query,
    setQuery,
    handleToggle,
    toggleExpand,
    handleClose: closeDropdown,
  } = useDropdownState(value);
  const filtered = useDistrictFilter(data, query);

  const handleClose = useCallback(
    (event: Event) => {
      closeDropdown(event, anchorRef);
    },
    [closeDropdown]
  );

  return (
    <>
      <Typography variant="caption" sx={styles.label}>
        {t('Congressional District')}
      </Typography>

      <Box ref={anchorRef} data-test="congressional-district-picker">
        <SearchField
          query={query}
          setQuery={setQuery}
          placeholder={t('Search Congressional District')}
          open={open}
          handleToggle={handleToggle}
        />

        <DistrictDropdown
          open={open}
          anchorRef={anchorRef}
          filtered={filtered}
          expanded={expanded}
          selected={selected}
          query={query}
          toggleExpand={toggleExpand}
          handleSelectDistrict={handleSelectDistrict}
          handleClose={handleClose}
        />
      </Box>
    </>
  );
};
